// /saved — the caller's bookmarked-player list.
//
// Renders the same PlayerCard grid /discover uses, sourced from
// GET /api/saved-players. Auth-required — anonymous viewers see the
// generic 401 surfaced by apiJson.

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiJson } from '../lib/api'
import PlayerCard, { type PlayerSearchResult } from '../components/PlayerCard'
import { PlayerCardGridSkeleton } from '../components/Skeleton'
import type { PlayerPosition } from '../types'

interface ListResponse {
  results: PlayerSearchResult[]
}

const POSITIONS: PlayerPosition[] = ['PG', 'SG', 'SF', 'PF', 'C']

export default function SavedPlayers() {
  const [results, setResults] = useState<PlayerSearchResult[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPositions, setSelectedPositions] = useState<Set<PlayerPosition>>(new Set())

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErr(null)
    apiJson<ListResponse>('/api/saved-players')
      .then((data) => {
        if (cancelled) return
        setResults(data.results)
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'unknown_error')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Client-side filtering: name search + position
  const filteredResults = useMemo(() => {
    let filtered = results

    // Name search — case/diacritic-insensitive
    if (searchQuery) {
      const normalizedQuery = searchQuery
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
      filtered = filtered.filter((p) => {
        const normalizedName = p.name
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase()
        return normalizedName.includes(normalizedQuery)
      })
    }

    // Position filter
    if (selectedPositions.size > 0) {
      filtered = filtered.filter(
        (p) => p.position && selectedPositions.has(p.position),
      )
    }

    return filtered
  }, [results, searchQuery, selectedPositions])

  function togglePosition(pos: PlayerPosition) {
    setSelectedPositions((prev) => {
      const next = new Set(prev)
      if (next.has(pos)) next.delete(pos)
      else next.add(pos)
      return next
    })
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-hoop-text">
          Saved players
        </h1>
        <p className="text-sm text-hoop-text-3 mt-1">
          Your private shortlist. Only you see this list.
        </p>
      </header>

      {err && (
        <div className="border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-4 py-3 text-sm mb-4">
          {err}
        </div>
      )}

      {loading ? (
        <PlayerCardGridSkeleton />
      ) : results.length === 0 ? (
        <div className="border border-dashed border-hoop-border rounded-lg p-10 text-center">
          <p className="text-hoop-text-2 text-sm">
            You haven't saved any players yet.
          </p>
          <p className="mt-2 text-sm">
            <Link
              to="/discover"
              className="text-hoop-text-2 underline hover:text-hoop-text"
            >
              Find players via Discover
            </Link>
          </p>
        </div>
      ) : (
        <>
          {/* Search input */}
          <div className="mb-4">
            <input
              type="search"
              aria-label="Search saved players by name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name…"
              className="w-full rounded-md border border-hoop-border bg-hoop-surface px-3 py-2 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent"
            />
          </div>

          {/* Position filter chips */}
          <div className="mb-4 flex flex-wrap gap-2">
            {POSITIONS.map((pos) => (
              <button
                key={pos}
                type="button"
                onClick={() => togglePosition(pos)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  selectedPositions.has(pos)
                    ? 'bg-hoop-accent text-hoop-accent-fg font-medium'
                    : 'bg-hoop-surface border border-hoop-border text-hoop-text-2 hover:border-hoop-accent'
                }`}
              >
                {pos}
              </button>
            ))}
          </div>

          {filteredResults.length === 0 ? (
            <div className="border border-dashed border-hoop-border rounded-lg p-10 text-center">
              <p className="text-hoop-text-2 text-sm">
                No saved players match &ldquo;{searchQuery || 'your filters'}&rdquo;.
              </p>
            </div>
          ) : (
            <>
              <div className="text-xs text-hoop-text-3 mb-3">
                {filteredResults.length} of {results.length} saved player
                {results.length === 1 ? '' : 's'}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredResults.map((r) => (
                  <PlayerCard key={r.id} player={r} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </main>
  )
}
